import { Link } from "react-router-dom";
import type { Comic } from "@/types/comic";
import { categories } from "@/mocks/categories";
import { useDownloadNotice } from "@/components/feature/DownloadNoticeProvider";

const categoryName = (slug: string) => categories.find((item) => item.slug === slug)?.name ?? slug;

interface ComicCardProps {
  comic: Comic;
  compact?: boolean;
}

export default function ComicCard({ comic, compact = false }: ComicCardProps) {
  const { show } = useDownloadNotice();
  const primaryCategory = comic.categories[0];

  return (
    <article className="group flex h-full flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors duration-300 hover:border-primary-300">
      <Link
        to={`/detail/${comic.id}`}
        className="relative block w-full overflow-hidden bg-background-100"
        style={{ aspectRatio: "3 / 4" }}
      >
        <img
          src={comic.cover}
          alt={`${comic.title} 漫画封面`}
          title={`${comic.title} 禁漫天堂漫画下载`}
          className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-2 top-2 rounded-md bg-primary-500/90 px-2 py-0.5 text-[11px] font-medium text-background-50">
          {comic.status}
        </span>
        <span className="absolute right-2 top-2 flex items-center gap-1 rounded-md bg-foreground-950/70 px-2 py-0.5 text-[11px] font-medium text-background-50">
          <i className="ri-star-fill text-accent-400" />
          {comic.rating.toFixed(1)}
        </span>
      </Link>

      <div className="flex flex-1 flex-col p-3 md:p-4">
        <Link to={`/detail/${comic.id}`} className="block">
          <h3 className={`truncate font-bold text-foreground-950 transition-colors group-hover:text-primary-600 ${compact ? "text-sm" : "text-sm md:text-base"}`}>
            {comic.title}
          </h3>
        </Link>

        <p className="mt-1 truncate text-xs text-foreground-500">
          {comic.author} · {comic.region}
        </p>

        {!compact && (
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-foreground-600">
            {comic.summary}
          </p>
        )}

        <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-foreground-500">
          <span className="flex items-center gap-1">
            <i className="ri-download-cloud-2-line text-accent-600" />
            {comic.downloads.toLocaleString()}
          </span>
          <span className="h-3 w-px bg-background-300" />
          <span className="flex items-center gap-1">
            <i className="ri-user-3-line" />
            {categoryName(primaryCategory)}
          </span>
        </div>

        <div className="mt-3 flex items-center gap-2 pt-1">
          <Link
            to={`/detail/${comic.id}`}
            className="flex flex-1 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
          >
            <i className="ri-information-line" />
            详情
          </Link>
          <button
            type="button"
            onClick={() => show(`${comic.title} · 第 ${comic.chapters.length} 卷合集（ZIP / PDF）`)}
            className="flex flex-1 cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-md bg-accent-500 px-3 py-2 text-xs font-semibold text-background-50 transition-colors hover:bg-accent-600"
          >
            <i className="ri-download-2-line" />
            下载
          </button>
        </div>
      </div>
    </article>
  );
}